import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { Icon } from '~/design-system/elements/Icon/Icon';
import type { FormatButton } from './types/formatting-toolbar.types';
import styles from './OutlinePanel.module.css';

interface FormattingToolbarProps {
  editor: Editor;
  buttons: FormatButton[];
}

export const FormattingToolbar = ({ editor, buttons }: FormattingToolbarProps) => {
  const [, setSelectionUpdate] = useState(0);

  // Re-render whenever the selection or doc changes so each button's `isActive(editor)` reflects the
  // CURRENT selection. Without this the menu rendered once with stale state on first show — selecting
  // body text showed the H1 button active until the next interaction.
  useEffect(() => {
    if (!editor) return;
    const rerender = () => setSelectionUpdate((prev) => prev + 1);
    editor.on('selectionUpdate', rerender);
    editor.on('transaction', rerender);
    return () => {
      editor.off('selectionUpdate', rerender);
      editor.off('transaction', rerender);
    };
  }, [editor]);

  const handleFormat = (action: (editor: Editor) => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    action(editor);
    const { to } = editor.state.selection;
    editor.commands.setTextSelection({ from: to, to });
    setSelectionUpdate((prev) => prev + 1);
  };

  return (
    <BubbleMenu editor={editor} className={styles.bubbleMenu} updateDelay={0}>
      {buttons.map((btn) => (
        <button type="button"
          key={btn.id}
          onMouseDown={handleFormat(btn.command)}
          className={btn.isActive(editor) ? styles.bubbleMenuButtonActive : styles.bubbleMenuButton}
          title={btn.title}
        >
          <Icon name={btn.icon} size={16} />
        </button>
      ))}
    </BubbleMenu>
  );
};
